import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { CreditCard, ExternalLink, Plus, Loader2, Unlink, TrendingUp } from "lucide-react";
import { navigateAdmin } from "@/lib/adminNav";
import { extractTmClientNumberFromCostCode } from "@/components/fleet-pulse/helpers";
import ECardSearchDialog, { ECardLite } from "./ECardSearchDialog";
import { OverviewTile } from "./CRMOverviewTile";

interface Props {
  accountId: string;
  defaultCostCode?: string | null;
  /** Where the TM tile goes when no TM cost code is set (the edit form). */
  onEditCostCode?: () => void;
}

// Renders the account's linked records as overview tiles (Total Mobility
// client number + linked eCards) -- drop it straight into a tile grid. The
// eCards tile opens a small manager for linking / opening / unlinking cards.
const CRMLinkedRecordsPanel: React.FC<Props> = ({ accountId, defaultCostCode, onEditCostCode }) => {
  const [linkedCards, setLinkedCards] = useState<ECardLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const tmNumber = extractTmClientNumberFromCostCode(defaultCostCode);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("ecards")
      .select("id, card_number, card_last4, customer_first_name, customer_last_name, email, taxicaller_account_id, status")
      .eq("taxicaller_account_id", accountId)
      .order("card_last4", { ascending: true });
    if (error) {
      console.error("Load linked eCards failed:", error);
      setLinkedCards([]);
    } else {
      setLinkedCards((data || []) as ECardLite[]);
    }
    setLoading(false);
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePickEcard = async (ec: ECardLite) => {
    setBusyId(ec.id);
    const { error } = await supabase.from("ecards").update({ taxicaller_account_id: accountId }).eq("id", ec.id);
    setBusyId(null);
    if (error) {
      toast({ title: "Link failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "eCard linked", description: `****${ec.card_last4 || "----"} now points at this CRM account.` });
    await load();
  };

  const handleUnlink = async (ec: ECardLite) => {
    setBusyId(ec.id);
    const { error } = await supabase.from("ecards").update({ taxicaller_account_id: null }).eq("id", ec.id);
    setBusyId(null);
    if (error) {
      toast({ title: "Unlink failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "eCard unlinked" });
    await load();
  };

  const cardLabel = (c: ECardLite) => `****${c.card_last4 || "----"}`;

  return (
    <>
      <OverviewTile
        icon={TrendingUp}
        title="Total Mobility Client"
        tone={tmNumber ? "info" : "neutral"}
        value={tmNumber ? `TM client #${tmNumber}` : "No TM cost code"}
        hint={
          tmNumber
            ? defaultCostCode || undefined
            : 'Set a default cost code like "TMClient 12345" to link'
        }
        actionLabel={tmNumber ? "Open TM coverage" : "Set cost code"}
        onClick={() => {
          if (tmNumber) navigateAdmin("fleetpulse", tmNumber);
          else onEditCostCode?.();
        }}
      />

      <OverviewTile
        icon={CreditCard}
        title="Linked eCards"
        tone={linkedCards.length > 0 ? "good" : "neutral"}
        value={loading ? "Loading..." : linkedCards.length > 0 ? `${linkedCards.length} linked` : "None linked"}
        hint={
          linkedCards.length > 0
            ? linkedCards.map(cardLabel).join(" · ")
            : "Link an eCard to this account"
        }
        actionLabel="Manage eCards"
        onClick={() => setManagerOpen(true)}
      />

      <Dialog open={managerOpen} onOpenChange={setManagerOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Linked eCards</DialogTitle>
            <DialogDescription>eCards pointing at this CRM account.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Link eCard
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center text-xs text-muted-foreground py-2">
                <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                Loading…
              </div>
            ) : linkedCards.length === 0 ? (
              <div className="text-xs text-muted-foreground italic py-2">
                No eCards linked. Use "Link eCard" to attach one.
              </div>
            ) : (
              <div className="space-y-1.5">
                {linkedCards.map((c) => {
                  const name = [c.customer_first_name, c.customer_last_name].filter(Boolean).join(" ") || "—";
                  const last4 = c.card_last4 || "----";
                  return (
                    <div
                      key={c.id}
                      className="flex flex-wrap items-center justify-between gap-2 bg-secondary/40 border border-border rounded-lg p-2.5"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <CreditCard className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-mono">****{last4}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {name}
                            {c.status && (
                              <Badge variant="outline" className="ml-2 text-[9px] py-0">
                                {c.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px]"
                          onClick={() => {
                            setManagerOpen(false);
                            navigateAdmin("ecard", last4);
                          }}
                          title="Open this card in eCards"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-[11px] text-red-400 hover:text-red-300"
                          disabled={busyId === c.id}
                          onClick={() => handleUnlink(c)}
                          title="Remove the link"
                        >
                          {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ECardSearchDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        excludeLinkedToAccountId={accountId}
        onPick={handlePickEcard}
      />
    </>
  );
};

export default CRMLinkedRecordsPanel;
